// src/components/character/CharacterCreateWizard.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { createCharacter } from '../../services/characterService';
import { PhotoIcon, CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { CHARACTER_TAGS } from '../../constants/characterTags';
import UploadGuidelinesModal from '../common/UploadGuidelinesModal';
import orifoliLogo from '../../assets/images/UI_orifoli_logo_black.png';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../services/firebase';
import { httpsCallable } from 'firebase/functions';

interface CharacterCreateWizardProps {
  isOpen: boolean;
  onClose: () => void;
  defaultCategory?: 'original' | 'vtuber';
}

// VTuber選択時はX認証ステップ(step 2)が追加され全7ステップ
// オリキャラ選択時は従来通り全6ステップ
const STEPS_ORIGINAL = 6;
const STEPS_VTUBER = 7; // Step2にX認証が入る

// X認証プロフィール情報
interface XProfile {
  xUserId: string;
  xUsername: string;
  xDisplayName: string;
  xPhotoURL: string;
}

const CharacterCreateWizard: React.FC<CharacterCreateWizardProps> = ({
  isOpen,
  onClose,
  defaultCategory,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('create');
  const { t: tTop } = useTranslation('top');
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [nameReading, setNameReading] = useState('');
  const [nameEnglish, setNameEnglish] = useState('');
  const [category, setCategory] = useState<'original' | 'vtuber'>(defaultCategory || 'original');
  const [bio, setBio] = useState('');
  const [catchcopy, setCatchcopy] = useState('');
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const [selectedPersonality, setSelectedPersonality] = useState<string | null>(null);
  const [selectedWorldview, setSelectedWorldview] = useState<string | null>(null);
  const selectedTags = [selectedGenre, selectedPersonality, selectedWorldview].filter(Boolean) as string[];
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [cardTheme, setCardTheme] = useState<'dark' | 'light'>('dark');
  const [themeColors, setThemeColors] = useState<string[]>(['#6366f1', '#ec4899', '#f59e0b']);

  // X認証状態
  const [xProfile, setXProfile] = useState<XProfile | null>(null);
  const [xVerifying, setXVerifying] = useState(false);
  const [xCheckingExisting, setXCheckingExisting] = useState(false);

  const imageInputRef = useRef<HTMLInputElement>(null);

  const totalSteps = category === 'vtuber' ? STEPS_VTUBER : STEPS_ORIGINAL;
  // VTuber時はstep2がX認証ステップ → 以降のステップは+1される
  const isVTuberXAuthStep = category === 'vtuber' && currentStep === 2;

  // Set default category when prop changes
  useEffect(() => {
    if (defaultCategory) {
      setCategory(defaultCategory);
    }
  }, [defaultCategory]);

  // Firestoreからxプロフィールを取得してstateにセットするヘルパー
  const fetchAndSetXProfile = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      if (data.xOAuthVerified && data.xUserId) {
        setXProfile({
          xUserId: data.xUserId,
          xUsername: data.xUsername || '',
          xDisplayName: data.xDisplayName || '',
          xPhotoURL: data.xPhotoURL || '',
        });
        if (data.xDisplayName) {
          setName(data.xDisplayName);
          setNameEnglish(data.xUsername ? `@${data.xUsername}` : '');
        }
        return true;
      }
    }
    return false;
  }, [user]);

  // VTuber選択時に既存のX連携状態をチェック
  const checkExistingXAuth = useCallback(async () => {
    if (!user || category !== 'vtuber') return;
    setXCheckingExisting(true);
    try {
      await fetchAndSetXProfile();
    } catch (err) {
      console.error('Failed to check X auth:', err);
    } finally {
      setXCheckingExisting(false);
    }
  }, [user, category, fetchAndSetXProfile]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setCurrentStep(1);
      setError(null);
      setXProfile(null);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // カテゴリ変更時にX認証チェック
  useEffect(() => {
    if (isOpen && category === 'vtuber' && user) {
      checkExistingXAuth();
    }
  }, [isOpen, category, user, checkExistingXAuth]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setIsImageLoading(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
        setIsImageLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  // X認証フローを開始
  const handleXAuth = useCallback(async () => {
    if (!user || xVerifying) return;
    setXVerifying(true);
    setError(null);

    try {
      // まずFirestoreを再チェック（既に連携済みならポップアップ不要）
      const alreadyLinked = await fetchAndSetXProfile();
      if (alreadyLinked) {
        setXVerifying(false);
        return;
      }

      // BroadcastChannelリスナーをセットアップ（認証完了を待つ）
      const channel = new BroadcastChannel('x_oauth_channel');

      // OAuth URLを生成してポップアップを開く
      const generateAuthUrl = httpsCallable(functions, 'generateXAuthUrl');
      const useLocalhost = window.location.hostname === 'localhost';
      const result = await generateAuthUrl({ mode: 'link', useLocalhost, app: 'orifoli' });
      const data = result.data as { authUrl: string };

      if (!data.authUrl) {
        channel.close();
        throw new Error('認証URLの生成に失敗しました');
      }

      const popup = window.open(data.authUrl, 'x_auth', 'width=600,height=700');
      if (!popup) {
        channel.close();
        localStorage.setItem('xAuthReturnPath', window.location.pathname);
        window.location.href = data.authUrl;
        return;
      }

      // ポップアップの完了 or 閉じを待つ
      const authPromise = new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          clearInterval(popupCheck);
          channel.close();
          reject(new Error('認証がタイムアウトしました'));
        }, 300000); // 5分

        // ポップアップが閉じられた場合の検知
        const popupCheck = setInterval(() => {
          if (popup.closed) {
            clearInterval(popupCheck);
            // 少し待ってからFirestoreを確認（ポップアップ側で処理完了している可能性）
            setTimeout(async () => {
              const linked = await fetchAndSetXProfile();
              clearTimeout(timeout);
              channel.close();
              if (linked) {
                resolve();
              } else {
                reject(new Error('認証がキャンセルされました'));
              }
            }, 1500);
          }
        }, 500);

        const handleMessage = (event: MessageEvent) => {
          if (event.data.type === 'X_AUTH_SUCCESS') {
            clearTimeout(timeout);
            clearInterval(popupCheck);
            channel.close();
            resolve();
          } else if (event.data.type === 'X_AUTH_CALLBACK' && event.data.code) {
            // ポップアップからコールバック委任された場合、直接exchangeXCodeを呼ぶ
            (async () => {
              try {
                const isProduction = import.meta.env.MODE === 'production';
                const functionsUrl = isProduction
                  ? 'https://asia-northeast1-figryne-prod.cloudfunctions.net/exchangeXCode'
                  : 'https://asia-northeast1-aerobic-pivot-440910-a3.cloudfunctions.net/exchangeXCode';
                const idToken = await user.getIdToken();
                const response = await fetch(functionsUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                  },
                  body: JSON.stringify({
                    code: event.data.code,
                    state: event.data.state,
                    useLocalhost,
                    app: 'orifoli',
                  }),
                });
                const resData = await response.json();
                if (response.ok && resData.success) {
                  clearTimeout(timeout);
                  clearInterval(popupCheck);
                  channel.close();
                  resolve();
                } else {
                  clearTimeout(timeout);
                  clearInterval(popupCheck);
                  channel.close();
                  reject(new Error(resData.message || '認証に失敗しました'));
                }
              } catch (e) {
                clearTimeout(timeout);
                clearInterval(popupCheck);
                channel.close();
                reject(e);
              }
            })();
          }
        };

        channel.onmessage = handleMessage;
      });

      await authPromise;

      // 認証成功 → Firestoreからプロフィール取得
      // 少し待ってから取得（Firestoreの書き込み反映待ち）
      await new Promise(resolve => setTimeout(resolve, 1000));
      await fetchAndSetXProfile();
    } catch (err) {
      console.error('X auth error:', err);
      setError(err instanceof Error ? err.message : '認証に失敗しました');
    } finally {
      setXVerifying(false);
    }
  }, [user, xVerifying, fetchAndSetXProfile]);

  // VTuber時のステップ番号をオリキャラのステップ番号にマッピング
  // VTuber: 1(カテゴリ) → 2(X認証) → 3(プロフィール) → 4(タグ) → 5(テーマ) → 6(キャッチ) → 7(確認)
  // Original: 1(カテゴリ) → 2(プロフィール) → 3(タグ) → 4(テーマ) → 5(キャッチ) → 6(確認)
  const getLogicalStep = () => {
    if (category !== 'vtuber') return currentStep;
    if (currentStep === 1) return 1; // カテゴリ
    if (currentStep === 2) return -1; // X認証（特殊ステップ）
    return currentStep - 1; // 3→2, 4→3, 5→4, 6→5, 7→6
  };

  const profileStep = category === 'vtuber' ? 3 : 2;

  const canProceed = () => {
    if (isVTuberXAuthStep) {
      return !!xProfile; // X認証済みのみ
    }
    if (currentStep === profileStep) {
      return name.trim().length > 0 && imageFile !== null;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep < totalSteps && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim() || !imageFile || isSubmitting || !user) return;

    setIsSubmitting(true);
    setError(null);
    setUploadProgress(0);
    setUploadStage('');

    try {
      const characterId = await createCharacter(
        user.uid,
        {
          name: name.trim(),
          nameReading: nameReading.trim() || undefined,
          nameEnglish: nameEnglish.trim() || undefined,
          category,
          bio: bio.trim() || undefined,
          catchcopy: catchcopy.trim() || undefined,
          tags: selectedTags,
          cardTheme,
          themeColors,
          isPublic: false,
          allowFanart: false,
          avatarFile: imageFile,
        },
        (progress, stage) => {
          setUploadProgress(progress);
          setUploadStage(stage);
        }
      );

      // Clear feed cache
      const cacheKeys = Object.keys(localStorage).filter(key => key.startsWith('oc_feed_'));
      cacheKeys.forEach(key => localStorage.removeItem(key));

      // キャラクタープロフィール登録完了トースト
      sessionStorage.setItem('pendingToast', t('toast.characterCreated', 'キャラクタープロフィール登録が完了しました。'));
      // 初回オンボーディングヒント表示フラグ（カード上の鉛筆マーク + 編集モーダルのSettings）
      sessionStorage.setItem('showCardEditHint', 'true');
      sessionStorage.setItem('showSettingsHint', 'true');

      // VTuberキャラクター作成時：FIGRYNEギフトモーダル表示フラグ
      if (category === 'vtuber') {
        sessionStorage.setItem('showFigryneGiftModal', characterId);
      }

      handleCloseAndReset();
      navigate(`/character/${characterId}`);
    } catch (err) {
      console.error('Error creating character:', err);
      let errorMessage = t('errors.createFailed');
      if (err instanceof Error) {
        // APIからの既知のエラーメッセージをi18n翻訳に変換
        if (err.message.includes('already registered as a VTuber')) {
          errorMessage = t('errors.xAlreadyRegistered');
        } else if (err.message.includes('X account verification')) {
          errorMessage = t('errors.xAuthRequired');
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
      setUploadStage('');
    }
  };

  const handleCloseAndReset = () => {
    setName('');
    setNameReading('');
    setNameEnglish('');
    setCategory(defaultCategory || 'original');
    setBio('');
    setCatchcopy('');
    setSelectedGenre(null);
    setSelectedPersonality(null);
    setSelectedWorldview(null);
    setImagePreview(null);
    setImageFile(null);
    setCardTheme('dark');
    setThemeColors(['#6366f1', '#ec4899', '#f59e0b']);
    setCurrentStep(1);
    setError(null);
    setXProfile(null);
    setXVerifying(false);
    onClose();
  };

  // Get display name and username from user
  const displayName = user?.displayName || 'Creator';
  const username = user?.email?.split('@')[0] || 'username';

  const canSubmit = name.trim() && imageFile && !isSubmitting && (category !== 'vtuber' || !!xProfile);

  if (!isOpen || !user) return null;

  // --- Step content renderers ---

  const renderStep1Category = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl font-bold text-gray-900">
          1. カテゴリーを選択
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          キャラクターの種類を選んでください
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setCategory('original')}
          className={`relative p-6 rounded-2xl border-2 transition-all ${
            category === 'original'
              ? 'border-purple-400 bg-purple-50'
              : 'border-gray-200 bg-white hover:bg-gray-50'
          }`}
        >
          {/* Palette / brush icon */}
          <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-purple-400 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
            </svg>
          </div>
          <div className="text-sm font-semibold text-gray-900 text-center">
            一次創作・オリキャラ
          </div>
          <div className="text-[10px] text-gray-400 mt-1 text-center">
            Original Character
          </div>
          {category === 'original' && (
            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
        <button
          type="button"
          onClick={() => setCategory('vtuber')}
          className={`relative p-6 rounded-2xl border-2 transition-all ${
            category === 'vtuber'
              ? 'border-purple-400 bg-purple-50'
              : 'border-gray-200 bg-white hover:bg-gray-50'
          }`}
        >
          {/* Video / camera icon for VTuber */}
          <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-blue-400 flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
          <div className="text-sm font-semibold text-gray-900 text-center">
            VTuber
          </div>
          <div className="text-[10px] text-gray-400 mt-1 text-center">
            Virtual YouTuber
          </div>
          {category === 'vtuber' && (
            <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </button>
      </div>
    </div>
  );

  // X認証ステップ（VTuber時のStep 2）
  const renderXAuthStep = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-xl font-bold text-gray-900">
          {t('xAuth.title')}
        </h3>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
          {t('xAuth.description')}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {t('xAuth.nameNotice')}
        </p>
      </div>

      {xCheckingExisting ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500 ml-2">確認中...</span>
        </div>
      ) : xProfile ? (
        // X認証済み表示
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {xProfile.xPhotoURL && (
                <img
                  src={xProfile.xPhotoURL}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                />
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {xProfile.xDisplayName}
                </p>
                <p className="text-xs text-green-600">
                  {t('xAuth.verifiedAs', { username: xProfile.xUsername })}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // X認証ボタン
        <div className="flex flex-col items-center py-4">
          <button
            type="button"
            onClick={handleXAuth}
            disabled={xVerifying}
            className="flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {xVerifying ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('xAuth.verifying')}
              </>
            ) : (
              <>
                {/* X logo */}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                {t('xAuth.button')}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderStepProfile = () => (
    <div className="space-y-4">
      {/* Image Upload */}
      <div>
        <button
          type="button"
          onClick={() => imageInputRef.current?.click()}
          className={`w-full h-32 rounded-xl border-2 border-dashed transition-all flex flex-col items-center justify-center gap-2 ${
            imageFile
              ? 'border-green-400 bg-green-50'
              : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400'
          }`}
        >
          {imageFile ? (
            <div className="flex items-center gap-3">
              {isImageLoading ? (
                <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <img
                  src={imagePreview || ''}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover"
                />
              )}
              <div className="text-left">
                <p className="text-sm text-green-600 font-medium">
                  {isImageLoading ? '読み込み中...' : '画像選択済み'}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  タップで変更
                </p>
              </div>
            </div>
          ) : (
            <>
              <PhotoIcon className="w-8 h-8 text-gray-400" />
              <span className="text-sm text-gray-500">{t('imagePlaceholder')}</span>
              <span className="text-[10px] text-gray-400">{t('imageHint', 'キービジュアル・立ち絵など背景付き PNG, JPEG')}</span>
              <span className="text-[10px] text-red-500">*必須</span>
            </>
          )}
        </button>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="hidden"
        />
      </div>

      {/* Name Input */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-700 font-medium">
            {t('name.placeholder')} <span className="text-red-500">*</span>
            {category === 'vtuber' && xProfile && (
              <LockClosedIcon className="w-3 h-3 inline-block ml-1 text-gray-400" />
            )}
          </span>
          <span className="text-[10px] text-gray-400">{name.length}/30</span>
        </div>
        {category === 'vtuber' && xProfile ? (
          <div className="relative">
            <input
              type="text"
              value={name}
              disabled
              className="w-full bg-gray-100 text-gray-600 text-sm rounded-lg px-3 py-2.5 outline-none border border-gray-200 cursor-not-allowed"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">
              {t('xAuth.nameLockedHint')}
            </span>
          </div>
        ) : (
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('name.placeholder')}
            className="w-full bg-white text-gray-900 text-sm rounded-lg px-3 py-2.5 placeholder-gray-400 outline-none border border-gray-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-colors"
            maxLength={30}
          />
        )}
      </div>

      {/* Name Reading */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-[11px] text-gray-500">{t('name.reading', 'よみがな')}</span>
          <span className="text-[10px] text-gray-400">{nameReading.length}/30</span>
        </div>
        <input
          type="text"
          value={nameReading}
          onChange={(e) => setNameReading(e.target.value)}
          placeholder={t('name.readingPlaceholder', 'ひらがな/カタカナ')}
          className="w-full bg-white text-gray-900 text-xs rounded-lg px-3 py-2 placeholder-gray-400 outline-none border border-gray-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-colors"
          maxLength={30}
        />
      </div>

      {/* Name English */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-[11px] text-gray-500">{t('name.english', '英語表記')}</span>
          <span className="text-[10px] text-gray-400">{nameEnglish.length}/50</span>
        </div>
        <input
          type="text"
          value={nameEnglish}
          onChange={(e) => setNameEnglish(e.target.value)}
          placeholder={t('name.englishPlaceholder', 'English Name')}
          className="w-full bg-white text-gray-900 text-xs rounded-lg px-3 py-2 placeholder-gray-400 outline-none border border-gray-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-colors"
          maxLength={50}
        />
      </div>

      {/* Bio */}
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-700 font-medium">{t('bio.label')}</span>
          <span className="text-[10px] text-gray-400">{bio.length}/100</span>
        </div>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder={t('bio.placeholder')}
          className="w-full bg-white text-gray-900 text-xs rounded-lg p-2.5 placeholder-gray-400 outline-none border border-gray-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-colors resize-none"
          rows={3}
          maxLength={100}
        />
      </div>
    </div>
  );

  const renderStep3Tags = () => (
    <div className="space-y-4">
      {/* Genre */}
      <div>
        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">{t('tags.genre')}</p>
        <div className="flex flex-wrap gap-1.5">
          {CHARACTER_TAGS.genre.options.map(option => (
            <button
              key={option.value}
              onClick={() => setSelectedGenre(prev => prev === option.value ? null : option.value)}
              className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                selectedGenre === option.value
                  ? 'bg-purple-500 text-white font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tTop(`tags.genre.${option.value}`, { defaultValue: option.label })}
            </button>
          ))}
        </div>
      </div>

      {/* Personality */}
      <div>
        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">{t('tags.personality')}</p>
        <div className="flex flex-wrap gap-1.5">
          {CHARACTER_TAGS.personality.options.map(option => (
            <button
              key={option.value}
              onClick={() => setSelectedPersonality(prev => prev === option.value ? null : option.value)}
              className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                selectedPersonality === option.value
                  ? 'bg-purple-500 text-white font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tTop(`tags.personality.${option.value}`, { defaultValue: option.label })}
            </button>
          ))}
        </div>
      </div>

      {/* Worldview */}
      <div>
        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">{t('tags.worldview')}</p>
        <div className="flex flex-wrap gap-1.5">
          {CHARACTER_TAGS.worldview.options.map(option => (
            <button
              key={option.value}
              onClick={() => setSelectedWorldview(prev => prev === option.value ? null : option.value)}
              className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                selectedWorldview === option.value
                  ? 'bg-purple-500 text-white font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tTop(`tags.worldview.${option.value}`, { defaultValue: option.label })}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep4Theme = () => (
    <div className="space-y-4">
      {/* Card Theme */}
      <div>
        <p className="text-xs text-gray-500 mb-2">カードの背景色を選んでください</p>
        <div className="flex gap-2">
          <button
            onClick={() => setCardTheme('dark')}
            className={`flex-1 py-2.5 rounded-lg text-xs font-medium transition-all ${
              cardTheme === 'dark'
                ? 'bg-gray-900 text-white ring-2 ring-purple-400'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {t('theme.dark')}
          </button>
          <button
            onClick={() => setCardTheme('light')}
            className={`flex-1 py-2.5 rounded-lg text-xs font-medium transition-all ${
              cardTheme === 'light'
                ? 'bg-white text-gray-900 ring-2 ring-purple-400 border border-gray-200'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {t('theme.light')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep5Catchcopy = () => (
    <div className="space-y-4">
      <div>
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-gray-700 font-medium">特徴・キャッチコピー</span>
          <span className="text-[10px] text-gray-400">{catchcopy.length}/20</span>
        </div>
        <input
          type="text"
          value={catchcopy}
          onChange={(e) => setCatchcopy(e.target.value)}
          placeholder="例：元気いっぱいの冒険者"
          className="w-full bg-white text-gray-900 text-sm rounded-lg p-2.5 placeholder-gray-400 outline-none border border-gray-200 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-colors"
          maxLength={20}
        />
        <p className="text-[10px] text-gray-400 mt-1">キャラクターを一言で表現してください（任意）</p>
      </div>
    </div>
  );

  const renderStep6Confirm = () => (
    <div className="space-y-4 text-center">
      <div className="text-sm text-gray-700">
        <span className="text-gray-500">カテゴリー：</span>
        {category === 'original' ? '一次創作・オリキャラ' : 'VTuber'}
      </div>
      <p className="text-sm text-gray-700">このイメージで登録します。</p>
      <p className="text-xs text-gray-500">
        登録ボタンを押す前に、
        <button
          type="button"
          onClick={() => setShowGuidelinesModal(true)}
          className="text-purple-500 hover:underline font-medium"
        >
          {t('guidelines.link')}
        </button>
        を確認してください
      </p>
    </div>
  );

  const renderCurrentStep = () => {
    if (category === 'vtuber') {
      // VTuber: 1→カテゴリ, 2→X認証, 3→プロフィール, 4→タグ, 5→テーマ, 6→キャッチ, 7→確認
      switch (currentStep) {
        case 1: return renderStep1Category();
        case 2: return renderXAuthStep();
        case 3: return renderStepProfile();
        case 4: return renderStep3Tags();
        case 5: return renderStep4Theme();
        case 6: return renderStep5Catchcopy();
        case 7: return renderStep6Confirm();
        default: return null;
      }
    } else {
      // Original: 1→カテゴリ, 2→プロフィール, 3→タグ, 4→テーマ, 5→キャッチ, 6→確認
      switch (currentStep) {
        case 1: return renderStep1Category();
        case 2: return renderStepProfile();
        case 3: return renderStep3Tags();
        case 4: return renderStep4Theme();
        case 5: return renderStep5Catchcopy();
        case 6: return renderStep6Confirm();
        default: return null;
      }
    }
  };

  // --- Card Preview ---
  const renderCardPreview = () => (
    <div className="bg-white p-2 rounded-2xl shadow-2xl">
      <div
        className="relative w-56 md:w-64 aspect-[2/3] rounded-xl overflow-hidden cursor-pointer group"
        onClick={() => imageInputRef.current?.click()}
      >
        {/* Full Bleed Image */}
        {isImageLoading ? (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
            <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-400 text-xs mt-3">読み込み中...</span>
          </div>
        ) : imagePreview ? (
          <img
            src={imagePreview}
            alt="Character"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
            <PhotoIcon className="w-10 h-10 text-gray-300 group-hover:text-gray-400 transition-colors" />
            <span className="text-gray-400 text-xs mt-2">{t('imagePlaceholder')}</span>
          </div>
        )}

        {/* Top Overlay - Logo */}
        <div className={`absolute top-0 left-0 right-0 pt-2.5 pb-8 px-4 bg-gradient-to-b ${
          cardTheme === 'dark' ? 'from-black/40 to-transparent' : 'from-white/60 to-transparent'
        }`}>
          <span className={`text-[10px] font-medium tracking-[0.2em] uppercase ${
            cardTheme === 'dark' ? 'text-white/80' : 'text-black/70'
          }`}>
            ORIFOLI
          </span>
        </div>

        {/* English Name - Top Right, Vertical */}
        {nameEnglish && (
          <div
            className="absolute top-4 right-1 z-10"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            <span className={`text-[10px] font-light tracking-wider whitespace-nowrap ${
              cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
            }`}>
              {nameEnglish}
            </span>
          </div>
        )}

        {/* Bottom Overlay - Character Info */}
        <div className={`absolute bottom-0 left-0 right-0 pt-20 pb-3 px-4 bg-gradient-to-t ${
          cardTheme === 'dark' ? 'from-black/60 via-black/40 to-transparent' : 'from-white/90 via-white/60 to-transparent'
        }`}>
          <p className={`text-lg font-semibold tracking-tight line-clamp-2 ${
            cardTheme === 'dark' ? 'text-white' : 'text-black/60'
          }`} style={{ lineHeight: '1.2' }}>
            {name || <span className={cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'}>Character Name</span>}
          </p>

          {nameReading && (
            <p className={`text-[9px] mt-0.5 ${
              cardTheme === 'dark' ? 'text-white/80' : 'text-black/80'
            }`}>
              {nameReading}
            </p>
          )}

          {bio && (
            <p className={`text-[10px] mt-1 line-clamp-2 leading-relaxed ${
              cardTheme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              {bio}
            </p>
          )}

          <div className="flex gap-1 mt-0.5 min-h-[14px]">
            {selectedTags.slice(0, 3).map(tag => {
              const translatedTag =
                tTop(`tags.genre.${tag}`, { defaultValue: '' }) ||
                tTop(`tags.personality.${tag}`, { defaultValue: '' }) ||
                tTop(`tags.worldview.${tag}`, { defaultValue: tag });
              return (
                <span
                  key={tag}
                  className={`text-[9px] ${cardTheme === 'dark' ? 'text-white/70' : 'text-gray-500'}`}
                >
                  #{translatedTag}
                </span>
              );
            })}
          </div>

          <div className={`border-t my-2 ${cardTheme === 'dark' ? 'border-white/20' : 'border-gray-300'}`} />

          <div className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 ${
              cardTheme === 'dark' ? 'bg-white/20' : 'bg-gray-200'
            }`}>
              {user?.photoURL ? (
                <img src={user.photoURL} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full bg-gradient-to-br ${
                  cardTheme === 'dark' ? 'from-white/30 to-white/10' : 'from-gray-300 to-gray-200'
                }`} />
              )}
            </div>
            <p className={`text-[9px] tracking-wide truncate ${
              cardTheme === 'dark' ? 'text-white/50' : 'text-gray-500'
            }`}>
              <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>CREATOR/</span>{displayName} <span className={cardTheme === 'dark' ? 'text-white/30' : 'text-gray-400'}>@{username}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // --- Step Indicator ---
  const renderStepIndicator = () => (
    <div className="flex items-center gap-2">
      {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => (
        <button
          key={step}
          onClick={() => {
            if (step < currentStep || (step === currentStep + 1 && canProceed())) {
              setCurrentStep(step);
            }
          }}
          className={`w-2.5 h-2.5 rounded-full transition-all ${
            step === currentStep
              ? 'bg-purple-500 scale-125'
              : step < currentStep
                ? 'bg-purple-300 hover:bg-purple-400 cursor-pointer'
                : 'bg-gray-200'
          }`}
        />
      ))}
      <span className="text-[10px] text-gray-400 ml-2">
        {currentStep}/{totalSteps}
      </span>
    </div>
  );

  return (
    <>
      <AnimatePresence>
        {isOpen && user && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="min-h-full flex items-start justify-center p-4 pt-4 pb-8">
              <motion.div
                className="w-full max-w-3xl mx-auto mt-4 sm:mt-8 bg-white rounded-2xl shadow-xl p-6"
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Logo + Title */}
                <div className="flex flex-col items-center mb-4">
                  <img src={orifoliLogo} alt="ORIFOLI" className="h-6" />
                  <p className="mt-2 text-sm text-gray-500 font-medium">{t('wizardTitle', 'キャラクタープロフィール登録')}</p>
                </div>

                {/* Header: Step Indicator + Close */}
                <div className="mb-5 flex justify-between items-center">
                  {renderStepIndicator()}
                  <button
                    onClick={handleCloseAndReset}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Step Title - shown above card row for profile steps onwards */}
                {(() => {
                  const ls = getLogicalStep();
                  if (ls < 2) return null; // カテゴリ選択やX認証ではタイトルなし
                  return (
                    <div className="mb-3">
                      <h3 className="text-lg font-bold text-gray-900">
                        {ls === 2 && '2. プロフィールカードの作成'}
                        {ls === 3 && `3. ${t('tags.label')}`}
                        {ls === 4 && '4. カードテーマ'}
                        {ls === 5 && '5. 特徴・キャッチコピー'}
                        {ls === 6 && '6. 確認'}
                      </h3>
                      {ls === 3 && (
                        <p className="text-sm text-gray-500 mt-1">タグを選択してください（各1つまで・任意）</p>
                      )}
                    </div>
                  );
                })()}

                {/* Main Content */}
                <div className={`flex flex-col ${!isVTuberXAuthStep && currentStep >= 2 ? 'md:flex-row' : ''} gap-4 md:gap-6 items-center md:items-start`}>
                  {/* Card Preview - shown from profile step onwards (not during X auth) */}
                  {!isVTuberXAuthStep && currentStep >= 2 && (
                    <div className="flex-shrink-0">
                      {renderCardPreview()}
                    </div>
                  )}

                  {/* Step Form */}
                  <div className={`w-full ${!isVTuberXAuthStep && currentStep >= 2 ? 'md:flex-1' : 'max-w-md mx-auto'} min-w-0`}>
                    <div className={`${!isVTuberXAuthStep && currentStep >= 2 ? 'bg-gray-50 rounded-2xl p-5' : isVTuberXAuthStep ? 'bg-gray-50 rounded-2xl p-5' : ''}`}>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={currentStep}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.2 }}
                        >
                          {renderCurrentStep()}
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* Error Message */}
                    {error && (
                      <div className="mt-3">
                        <p className="text-red-500 text-sm text-center">{error}</p>
                      </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="mt-4 flex justify-between items-center">
                      <button
                        onClick={handleBack}
                        disabled={currentStep === 1}
                        className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm transition-all ${
                          currentStep === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        戻る
                      </button>

                      {currentStep < totalSteps ? (
                        <button
                          onClick={handleNext}
                          disabled={!canProceed()}
                          className={`flex items-center gap-1 px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            canProceed()
                              ? 'bg-purple-500 text-white hover:bg-purple-400'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {currentStep === totalSteps - 1 ? '確認へ' : '次へ'}
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={handleSubmit}
                          disabled={!canSubmit}
                          className="bg-gray-900 text-white px-8 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center">
                              <svg
                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              {uploadStage === 'encrypting' ? t('submit.encrypting') :
                               uploadStage === 'uploading' ? t('submit.uploading') :
                               t('submit.registering')}
                            </span>
                          ) : (
                            t('submit.button')
                          )}
                        </button>
                      )}
                    </div>

                    {/* Progress bar */}
                    {isSubmitting && uploadProgress > 0 && (
                      <div className="mt-3 w-full">
                        <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-gray-400 text-center mt-1">
                          {uploadProgress}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guidelines Modal */}
      <UploadGuidelinesModal
        isOpen={showGuidelinesModal}
        onClose={() => setShowGuidelinesModal(false)}
      />
    </>
  );
};

export default CharacterCreateWizard;
